import {
  useRef,
  useState,
  type ReactNode,
  type TouchEvent
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { haptic } from '../lib/haptics';

interface SwipeRevealProps {
  children: ReactNode;
  actionLabel: string;
  ActionIcon: LucideIcon;
  destructive?: boolean;
  onAction: () => void;
}

const ACTION_WIDTH = 86;

export function SwipeReveal({
  children,
  actionLabel,
  ActionIcon,
  destructive = false,
  onAction
}: SwipeRevealProps) {
  const start = useRef({ x: 0, y: 0 });
  const horizontal = useRef(false);
  const suppressClick = useRef(false);
  const [offset, setOffset] = useState(0);

  function touchStart(event: TouchEvent<HTMLDivElement>) {
    const point = event.touches[0];
    if (!point) return;
    start.current = { x: point.clientX, y: point.clientY };
    horizontal.current = false;
    suppressClick.current = false;
  }

  function touchMove(event: TouchEvent<HTMLDivElement>) {
    const point = event.touches[0];
    if (!point) return;
    const dx = point.clientX - start.current.x;
    const dy = point.clientY - start.current.y;
    if (!horizontal.current && Math.abs(dx) > 9 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      horizontal.current = true;
    }
    if (!horizontal.current) return;
    suppressClick.current = true;
    const next = Math.max(-ACTION_WIDTH - 12, Math.min(0, dx));
    setOffset(next);
  }

  function touchEnd() {
    if (!horizontal.current) return;
    const open = offset < -ACTION_WIDTH * 0.48;
    setOffset(open ? -ACTION_WIDTH : 0);
    haptic(open ? 'light' : 'selection');
    window.setTimeout(() => { suppressClick.current = false; }, 80);
  }

  function runAction() {
    setOffset(0);
    onAction();
  }

  return (
    <div
      className={`swipe-reveal${offset ? ' is-open' : ''}`}
      onClickCapture={(event) => {
        if (!suppressClick.current) return;
        event.preventDefault();
        event.stopPropagation();
      }}
      onTouchStart={touchStart}
      onTouchMove={touchMove}
      onTouchEnd={touchEnd}
    >
      <button
        type="button"
        className={`swipe-reveal__action${destructive ? ' is-destructive' : ''}`}
        onClick={runAction}
        aria-label={actionLabel}
      >
        <ActionIcon />
        <span>{actionLabel}</span>
      </button>
      <div className="swipe-reveal__content" style={{ transform: `translateX(${offset}px)` }}>
        {children}
      </div>
    </div>
  );
}
